/**
 * Browser-side photo preparation before upload (DOM-only; the pure maths is in devicePhotos.ts).
 *
 * Why re-encode in the browser at all, when the controller strips and re-encodes anyway:
 *  - SPEED ON SITE: a 12 MP tablet photo is 3–6 MB; downscaled to 2560 px JPEG it is a few
 *    hundred KB. Installers upload over hotel Wi-Fi they are still commissioning, or LTE.
 *  - HEIC: a browser that CAN decode HEIC (Safari) turns it into a JPEG here, so the server
 *    gets a format it can fully decode, strip and thumbnail. A browser that cannot decode it
 *    uploads the original, and the server's HEIF metadata scrub handles it.
 *  - Canvas output carries no EXIF at all, so location data never leaves the device on this path.
 *
 * createImageBitmap applies the EXIF orientation by default ("from-image"), so the pixels are
 * already upright before the orientation tag is dropped.
 */
import { CLIENT_MAX_EDGE, fitWithin } from "./devicePhotos";

/** JPEG quality for the browser re-encode (matches the controller's 85). */
const JPEG_QUALITY = 0.85;

/**
 * Downscale + re-encode `file` to JPEG when this browser can decode it; otherwise return the
 * original file unchanged. Never throws.
 */
export async function prepareForUpload(file: File): Promise<Blob> {
  if (typeof createImageBitmap !== "function") return file;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const { width, height } = fitWithin(bitmap.width, bitmap.height, CLIENT_MAX_EDGE);
    if (!width || !height) return file;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    // JPEG has no alpha: flatten transparency (a PNG screenshot) onto white, not black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    // Keep the original if re-encoding somehow made it bigger (a tiny, already-small JPEG).
    if (!blob) return file;
    return blob.size < file.size || file.type !== "image/jpeg" ? blob : file;
  } catch {
    // Undecodable here (e.g. HEIC outside Safari): let the server deal with the original.
    return file;
  } finally {
    bitmap?.close();
  }
}
